import { useRef, useEffect } from 'react';

export default function SalesFunnelPage() {
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schemaId = 'schema-segmentation-strategy';
    const existing = document.getElementById(schemaId);
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = schemaId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'L2L Segmentation Strategy \u2014 Liability to Liquidity',
      description: 'Three-segment customer strategy for migrating sportsbook users to CFTC-regulated prediction markets. Whales, untapped states, and recreational bettors.',
      url: `${siteUrl}/segmentation-strategy`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Segmentation Strategy', item: `${siteUrl}/segmentation-strategy` },
        ],
      },
    });
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  const handleDownload = () => {
    window.print();
  };

  return (
    <div className="min-h-screen" style={{ background: '#1e1e1e' }}>
      {/* Print Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

        @media print {
          @page {
            size: A4;
            margin: 0.5in;
          }
          body {
            background: white !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .no-print {
            display: none !important;
          }
          .print-page {
            background: white !important;
          }
          .print-card {
            break-inside: avoid;
          }
          .print-force-white {
            background: white !important;
            color: #111 !important;
          }
        }
      `}</style>

      {/* Top Bar - no print */}
      <div className="no-print bg-amber-500 text-slate-900 px-4 py-2">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-center">
          <i className="ri-alert-line text-lg"></i>
          <span className="font-bold text-sm md:text-base tracking-wide uppercase">
            Demo Only — Experience the Future of Sports Betting
          </span>
        </div>
      </div>

      {/* Nav Bar - no print */}
      <nav className="no-print sticky top-0 z-50 backdrop-blur-lg border-b border-slate-700/50" style={{ background: 'rgba(43,43,43,0.97)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
          <a href="/" className="flex items-center gap-2 flex-shrink-0">
            <img
              src="https://static.readdy.ai/image/bc21bbd943df20a73e2c62e0eb37a79f/356d7ca1b6cdb5f780c70d0d0bd7c02d.png"
              alt="L2L Logo"
              className="h-10 w-auto object-contain"
            />
          </a>
          <div className="hidden md:flex items-center gap-1">
            {[
              { label: 'Home', href: '/', icon: 'ri-home-line' },
              { label: 'March Madness', href: '/march-madness', icon: 'ri-basketball-line' },
              { label: 'Markets', href: '/markets', icon: 'ri-bar-chart-2-line' },
              { label: 'Why L2L', href: '/why-rtj', icon: 'ri-question-line' },
            ].map(item => (
              <a key={item.href} href={item.href}>
                <div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white">
                  <i className={`${item.icon} w-4 h-4 flex items-center justify-center`}></i>
                  {item.label}
                </div>
              </a>
            ))}
          </div>
          <button
            onClick={handleDownload}
            className="no-print flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-white font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap text-sm shadow-lg shadow-teal-500/25"
          >
            <i className="ri-download-2-line"></i>
            Download PDF
          </button>
        </div>
      </nav>

      {/* Printable Content */}
      <div ref={printRef} className="print-page" style={{ background: '#1e1e1e' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10 sm:py-16">

          {/* Header */}
          <div className="text-center mb-12 print-card">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4 text-sm font-semibold tracking-widest uppercase"
              style={{ background: 'rgba(20,184,166,0.15)', color: '#2dd4bf', border: '1px solid rgba(20,184,166,0.3)' }}>
              <i className="ri-bar-chart-box-line"></i>
              Liability to Liquidity — Strategic Framework
            </div>
            <h1 className="text-4xl sm:text-5xl font-black mb-4 leading-tight" style={{ color: '#f1f5f9', fontFamily: 'Inter, sans-serif' }}>
              Legacy Sportsbooks<br />
              <span style={{ color: '#2dd4bf' }}>Segmentation Strategy</span>
            </h1>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: '#94a3b8' }}>
              Three customer segments. Two platforms. One industry-defining opportunity.
            </p>

            {/* Divider */}
            <div className="flex items-center gap-4 mt-8">
              <div className="flex-1 h-px" style={{ background: 'rgba(71,85,105,0.5)' }}></div>
              <div className="w-2 h-2 rounded-full" style={{ background: '#2dd4bf' }}></div>
              <div className="flex-1 h-px" style={{ background: 'rgba(71,85,105,0.5)' }}></div>
            </div>
          </div>

          {/* Visual Funnel - Chevrons */}
          <div className="flex flex-col items-center gap-0 mb-16 print-card">
            {/* Top Label */}
            <div className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: '#64748b' }}>
              TOTAL ADDRESSABLE MARKET
            </div>

            {/* Segment 1 - Widest */}
            <FunnelSegment
              width="100%"
              color="#0f766e"
              lightColor="#14b8a6"
              bgGlow="rgba(20,184,166,0.08)"
              borderColor="rgba(20,184,166,0.4)"
              index={1}
              platform="Prediction Market"
              platformColor="#2dd4bf"
              title="Whales into Sales"
              tagline="Turn High-Dollar Players From Liabilities Into Liquidity"
              icon="ri-vip-crown-2-line"
              iconBg="rgba(20,184,166,0.15)"
              iconColor="#2dd4bf"
              bullets={[
                { icon: 'ri-arrow-right-circle-fill', color: '#2dd4bf', text: 'Rather than limiting big winners, redirect them to FD Predicts' },
                { icon: 'ri-arrow-right-circle-fill', color: '#2dd4bf', text: 'Bring back the whales with commission-free trades — not gimmicky bonuses' },
                { icon: 'ri-arrow-right-circle-fill', color: '#2dd4bf', text: 'Create a dedicated desktop interface to attract heavy traders' },
              ]}
              accentStat={{ label: 'High-Value Players', value: 'VIP Tier' }}
            />

            {/* Funnel Arrow */}
            <FunnelArrow color="#334155" />

            {/* Segment 2 - Medium */}
            <FunnelSegment
              width="84%"
              color="#0e4d6a"
              lightColor="#06b6d4"
              bgGlow="rgba(6,182,212,0.07)"
              borderColor="rgba(6,182,212,0.35)"
              index={2}
              platform="Prediction Market"
              platformColor="#67e8f9"
              title="The Untapped 35%"
              tagline="States Where Sports Betting Isn't Legal"
              icon="ri-map-pin-2-line"
              iconBg="rgba(6,182,212,0.15)"
              iconColor="#67e8f9"
              bullets={[
                { icon: 'ri-arrow-right-circle-fill', color: '#67e8f9', text: '90+ million Americans in California, Texas, and Florida' },
                { icon: 'ri-arrow-right-circle-fill', color: '#67e8f9', text: 'Novelty segment that can be enticed by sign-up bonuses' },
                { icon: 'ri-arrow-right-circle-fill', color: '#67e8f9', text: 'Same Customer Acquisition strategy as the original customer base' },
                { icon: 'ri-arrow-right-circle-fill', color: '#67e8f9', text: 'Allow users to transfer funds directly from their existing Fantasy account' },
              ]}
              accentStat={{ label: 'Potential New Users', value: '90M+' }}
            />

            {/* Funnel Arrow */}
            <FunnelArrow color="#334155" />

            {/* Segment 3 - Narrowest */}
            <FunnelSegment
              width="68%"
              color="#451a03"
              lightColor="#f59e0b"
              bgGlow="rgba(245,158,11,0.07)"
              borderColor="rgba(245,158,11,0.35)"
              index={3}
              platform="Legacy Sportsbook"
              platformColor="#fcd34d"
              title="The Recreational Bettor"
              tagline="Legacy Sportsbook's High-Margin Customers"
              icon="ri-gamepad-line"
              iconBg="rgba(245,158,11,0.15)"
              iconColor="#fcd34d"
              bullets={[
                { icon: 'ri-arrow-right-circle-fill', color: '#fcd34d', text: 'Status quo — keep using the legacy platform' },
                { icon: 'ri-arrow-right-circle-fill', color: '#fcd34d', text: "Sportsbook's bread and butter — high margin customers" },
                { icon: 'ri-arrow-right-circle-fill', color: '#fcd34d', text: 'Keep offering enticing same game parlays' },
              ]}
              accentStat={{ label: 'Margin Profile', value: 'High-Yield' }}
            />

            {/* Bottom tip */}
            <div className="w-0 h-0" style={{
              borderLeft: '24px solid transparent',
              borderRight: '24px solid transparent',
              borderTop: '18px solid rgba(245,158,11,0.35)'
            }}></div>
          </div>

          {/* Strategy Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12 print-card">
            <SummaryCard
              color="#2dd4bf"
              bgColor="rgba(20,184,166,0.08)"
              borderColor="rgba(20,184,166,0.3)"
              icon="ri-vip-crown-2-line"
              label="Segment 1"
              title="Whales"
              desc="Redirect elite bettors to FD Predicts for zero-juice trading"
            />
            <SummaryCard
              color="#67e8f9"
              bgColor="rgba(6,182,212,0.08)"
              borderColor="rgba(6,182,212,0.3)"
              icon="ri-map-2-line"
              label="Segment 2"
              title="Untapped 35%"
              desc="Capture 90M Americans in non-legalized states via prediction markets"
            />
            <SummaryCard
              color="#fcd34d"
              bgColor="rgba(245,158,11,0.08)"
              borderColor="rgba(245,158,11,0.3)"
              icon="ri-gamepad-line"
              label="Segment 3"
              title="Recreational"
              desc="Retain high-margin customers with status quo parlay offerings"
            />
          </div>

          {/* Platform Allocation Bar */}
          <div className="rounded-xl p-6 mb-12 print-card" style={{ background: 'rgba(30,41,59,0.8)', border: '1px solid rgba(71,85,105,0.4)' }}>
            <h3 className="text-sm font-bold tracking-widest uppercase mb-5" style={{ color: '#64748b' }}>
              Platform Allocation Strategy
            </h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span style={{ color: '#e2e8f0' }} className="font-semibold">FD Predicts (Prediction Market)</span>
                  <span style={{ color: '#2dd4bf' }} className="font-bold">Segments 1 + 2</span>
                </div>
                <div className="h-3 rounded-full overflow-hidden" style={{ background: 'rgba(15,23,42,0.6)' }}>
                  <div className="h-full rounded-full" style={{ width: '67%', background: 'linear-gradient(90deg, #0f766e, #06b6d4)' }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span style={{ color: '#e2e8f0' }} className="font-semibold">Legacy Sportsbook Platform</span>
                  <span style={{ color: '#fcd34d' }} className="font-bold">Segment 3</span>
                </div>
                <div className="h-3 rounded-full overflow-hidden" style={{ background: 'rgba(15,23,42,0.6)' }}>
                  <div className="h-full rounded-full" style={{ width: '33%', background: 'linear-gradient(90deg, #b45309, #f59e0b)' }}></div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center pt-6 print-card" style={{ borderTop: '1px solid rgba(71,85,105,0.4)' }}>
            <div className="flex items-center justify-center gap-3 mb-3">
              <img
                src="https://static.readdy.ai/image/bc21bbd943df20a73e2c62e0eb37a79f/356d7ca1b6cdb5f780c70d0d0bd7c02d.png"
                alt="L2L Logo"
                className="h-8 w-auto object-contain opacity-70"
              />
            </div>
            <p className="text-xs" style={{ color: '#475569' }}>
              Liability to Liquidity — Confidential Strategic Document · {new Date().getFullYear()}
            </p>
          </div>

        </div>
      </div>

      {/* Floating Download Button - no print */}
      <div className="no-print fixed bottom-6 right-6 z-50">
        <button
          onClick={handleDownload}
          className="flex items-center gap-2 px-6 py-3 font-bold rounded-xl cursor-pointer whitespace-nowrap text-sm shadow-2xl transition-all hover:scale-105"
          style={{ background: 'linear-gradient(135deg, #0f766e, #0891b2)', color: 'white', boxShadow: '0 8px 32px rgba(20,184,166,0.4)' }}
        >
          <i className="ri-download-2-line text-lg"></i>
          Save as PDF
        </button>
      </div>
    </div>
  );
}

/* ── Sub-components ──────────────────────────────────── */

interface BulletItem {
  icon: string;
  color: string;
  text: string;
}

interface FunnelSegmentProps {
  width: string;
  color: string;
  lightColor: string;
  bgGlow: string;
  borderColor: string;
  index: number;
  platform: string;
  platformColor: string;
  title: string;
  tagline: string;
  icon: string;
  iconBg: string;
  iconColor: string;
  bullets: BulletItem[];
  accentStat: { label: string; value: string };
}

function FunnelSegment({
  width, bgGlow, borderColor, index, platform, platformColor,
  title, tagline, icon, iconBg, iconColor, bullets, accentStat
}: FunnelSegmentProps) {
  return (
    <div
      className="rounded-2xl p-6 transition-all hover:scale-[1.01] print-card"
      style={{
        width,
        background: bgGlow,
        border: `1px solid ${borderColor}`,
        backdropFilter: 'blur(12px)',
      }}
    >
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: iconBg }}
        >
          <i className={`${icon} text-2xl`} style={{ color: iconColor }}></i>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span
              className="text-xs font-bold tracking-widest uppercase px-2.5 py-0.5 rounded-full"
              style={{ background: `${platformColor}20`, color: platformColor, border: `1px solid ${platformColor}40` }}
            >
              Segment {index} · {platform}
            </span>
          </div>

          <h2
            className="text-xl sm:text-2xl font-black mb-1 leading-tight"
            style={{ color: '#f1f5f9', fontFamily: 'Inter, sans-serif' }}
          >
            {title}
          </h2>
          <p className="text-sm font-semibold mb-4" style={{ color: platformColor }}>
            {tagline}
          </p>

          {/* Bullets */}
          <ul className="space-y-2 mb-4">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <i className={`${b.icon} text-base flex-shrink-0 mt-0.5`} style={{ color: b.color }}></i>
                <span className="text-sm leading-relaxed" style={{ color: '#cbd5e1' }}>{b.text}</span>
              </li>
            ))}
          </ul>

          {/* Accent Stat */}
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold"
            style={{ background: `${platformColor}15`, color: platformColor, border: `1px solid ${platformColor}30` }}
          >
            <i className="ri-bar-chart-fill"></i>
            {accentStat.label}: {accentStat.value}
          </div>
        </div>
      </div>
    </div>
  );
}

function FunnelArrow({ color }: { color: string }) {
  return (
    <div className="flex flex-col items-center my-0">
      <div className="w-px h-4" style={{ background: color }}></div>
      <div className="w-0 h-0" style={{
        borderLeft: '12px solid transparent',
        borderRight: '12px solid transparent',
        borderTop: `10px solid ${color}`
      }}></div>
    </div>
  );
}

interface SummaryCardProps {
  color: string;
  bgColor: string;
  borderColor: string;
  icon: string;
  label: string;
  title: string;
  desc: string;
}

function SummaryCard({ color, bgColor, borderColor, icon, label, title, desc }: SummaryCardProps) {
  return (
    <div
      className="rounded-xl p-5 print-card"
      style={{ background: bgColor, border: `1px solid ${borderColor}` }}
    >
      <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ background: `${color}20` }}>
        <i className={`${icon} text-xl`} style={{ color }}></i>
      </div>
      <div className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: `${color}99` }}>{label}</div>
      <div className="font-black text-lg mb-2" style={{ color: '#f1f5f9' }}>{title}</div>
      <p className="text-sm leading-relaxed" style={{ color: '#94a3b8' }}>{desc}</p>
    </div>
  );
}
